/// <reference types="zone.js" />

import { Component, OnInit, HostBinding, Optional, Inject } from '@angular/core';
import { AppConfig, Shell, UserAgent, Redirection, SiteWideAlert } from '../common';
import { UserService } from '../user';
import { AppApi, ApiConfiguration, ApiUser } from '../api';
import { Router, NavigationEnd } from '@angular/router';
import { REQUEST, RESPONSE } from '@nguniversal/express-engine/tokens';
import * as express from '../common/express-ssr';
import { environment } from 'environments/environment';
import { isClientSide, isServerSide } from '../environment-utils';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
    public iframeMode = false;

    constructor(
        private userService: UserService,
        private appConfig: AppConfig,
        private appApi: AppApi,
        private shell: Shell,
        private userAgent: UserAgent,
        private redirection: Redirection,
        private apiConfig: ApiConfiguration,
        private router: Router,
        @Inject(REQUEST) @Optional()
        private request: express.ExpressRequest,
        @Inject(RESPONSE) @Optional()
        private response: express.ExpressResponse
    ) {
        if (request) {
            this.apiConfig.globalHeaders['X-User-Agent'] = request.header('user-agent');
            this.apiConfig.globalHeaders['X-TYT-On-Behalf-Of'] = request.ip;
            if (Zone.current.get('requestId'))
                this.apiConfig.globalHeaders['X-TYT-Trace'] = Zone.current.get('requestId');
        }

        if (typeof window !== 'undefined') {
            window['appEnvironment'] = environment;
        }

        if (typeof window !== 'undefined') {
            if (window.parent !== window)
                document.querySelector('html').classList.add('embedded');
            else
                document.querySelector('html').classList.remove('embedded');

            let initialPageView = true;

            this.router.events.subscribe(ev => {
                if (ev instanceof NavigationEnd) {
                    if (typeof window !== 'undefined') {
                        try {
                            let theme = this.router.routerState.snapshot.root.queryParamMap.get('theme');
                            if (theme) {
                                if (theme === 'dark') {
                                    document.querySelector('html').classList.add('dark-theme');
                                } else {
                                    document.querySelector('html').classList.remove('dark-theme');
                                }
                            }
                        } catch (e) {
                            console.error(`Caught error detecting theme mode: ${e.message}`);
                            console.error(e);
                        }
                    }

                    if (initialPageView) {
                        console.log(`[--] Page View (Initial)`);
                        initialPageView = false;
                    } else {
                        console.log(`[--] Page View (SPA): ${window.location.href}`);

                        // GTM+GA+SPA is a recipe for disaster. Use GA/FP directly, GTM will be used for all tags except GA.

                        if (typeof window['ga'] !== 'undefined') {
                            let ga = window['ga'];
                            ga('set', 'page', ev.url);
                            ga('send', 'pageview');
                            console.log(`[GA] Page View`);
                        }

                        // Facebook analytics

                        if (typeof window['xfbq'] !== 'undefined') {
                            let xfbq = window['xfbq'];
                            xfbq('track', "PageView");
                            console.log(`[FP] Page View`);
                        }
                    }

                }
            })
        }
    }

    switchBack() {

        this.userService.switchBack();
        this.redirection.go(`${environment.urls.admin}/frontend/assumed`);
    }

    @HostBinding('class.chromeless')
    get chromeless() {
        return this._chromeless;
    }

    set chromeless(value: boolean) {
        this._chromeless = value;

        // This caused an issue where privacy policy is listed white-on-white in Android app. 
        // I _think_ this was added to support the unified Settings system where Accounts would be rendered in an 
        // iframe, but I'm not sure. In any case it's a bad idea.
        //
        // if (typeof window !== 'undefined') {
        //   if (value) {
        //     document.body.classList.add('transparent-bg');
        //   } else {
        //     document.body.classList.remove('transparent-bg');
        //   }
        // }
    }

    private _chromeless = false;

    get isServer() { return isServerSide(); }
    get clientSide() { return isClientSide(); }

    async ngOnInit() {
        this.userService.init();

        if (typeof window !== 'undefined') {
            if (window.location.search.startsWith('?chromeless')) {
                // Chromeless mode!
                this.shell.chromeless = true;
            }
        }

        try {
            this.appConfig.appStatus = await this.appApi.getStatus('0.0.0', <any>'accounts').toPromise();
            this.appConfig.fireAppStatusReady();
        } catch (e) {
            console.error(`While getting appconfig: Caught error:`);
            console.error(e);
            console.warn(`Failed to contact backend server, cannot configure app!`);
        }

        // Add the site-wide alert if we have one

        if (this.appConfig.appStatus && this.appConfig.appStatus.settings && this.appConfig.appStatus.settings.site_alert_msg) {
            this.shell.addAlert({
                id: 'notice',
                message: this.appConfig.appStatus.settings.site_alert_msg,
                url: this.appConfig.appStatus.settings.site_alert_url
            });
        }

        this.userService.userChanged.subscribe(user => this.user = user);
        this.shell.chromelessChanged.subscribe(chromeless => this.chromeless = chromeless);
    }

    user: ApiUser;


    private _noPushState: boolean = false;

    browser: string;
    os: string;

    markUserAgent() {
        this.browser = this.userAgent.browser;
        this.os = this.userAgent.os;

        if (typeof document !== 'undefined') {
            document.documentElement.classList.add(`${this.browser}-browser`);
            document.documentElement.classList.add(`${this.os}-os`);
        }
    }
}
